import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, dashboardFor } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Menu, X, LogOut, LayoutDashboard, Bell, UserCircle, Repeat, Check, Sparkles } from "lucide-react";

// Role-scoped nav links. Each role sees ONLY the community sections that belong to it.
// Parent gets the full browse experience; providers see just the surface they operate on.
const NAV_LINKS_BY_ROLE = {
  parent: [
    { to: "/vendors", label: "Vendors" },
    { to: "/events", label: "Events" },
    { to: "/meetups", label: "Meet-ups" },
    { to: "/resources?tab=directory", label: "Marketplace" },
    { to: "/resources", label: "Resources" },
  ],
  vendor: [
    { to: "/vendors", label: "Vendors" },
  ],
  event_creator: [
    { to: "/events", label: "Events" },
  ],
  marketplace_user: [
    { to: "/resources?tab=directory", label: "Marketplace" },
  ],
  admin: [
    { to: "/vendors", label: "Vendors" },
    { to: "/events", label: "Events" },
    { to: "/meetups", label: "Meet-ups" },
    { to: "/resources?tab=directory", label: "Marketplace" },
    { to: "/resources", label: "Resources" },
  ],
};

const linksFor = (user) => (user && NAV_LINKS_BY_ROLE[user.role]) || [];

// Profile-setup routes render a chrome-free header (logo only). Vendors get the same
// treatment while their profile isn't complete or their account is still pending approval.
const SETUP_PATHS = ["/profile-setup", "/event-creator-setup", "/marketplace-setup"];
const isSetupStep = (user, pathname) => {
  if (SETUP_PATHS.includes(pathname)) return true;
  if (pathname === "/vendor-portal" && user?.role === "vendor" && (!user.profile_completed || user.vendor_status !== "approved")) return true;
  return false;
};

function NotificationsBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (!user) return null;
  const unread = items.filter((n) => !n.read).length;

  const openNotif = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.notification_id}/read`); } catch { /* ignore */ }
    }
    setOpen(false);
    if (n.link) navigate(n.link);
    load();
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); load(); } catch { /* ignore */ }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-full hover:bg-sage-light transition-colors text-forest/70" data-testid="notif-bell-btn" aria-label="Notifications">
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-terracotta text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1" data-testid="notif-badge">
              {unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 rounded-2xl bg-cream border-sage/20 p-0 shadow-lg" data-testid="notif-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-sage/15">
          <span className="font-heading font-bold text-forest text-sm">Notifications</span>
          {unread > 0 && (
            <button onClick={markAll} className="text-xs font-bold text-sage-dark hover:underline" data-testid="notif-mark-all">
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 && (
            <p className="text-sm text-forest/50 text-center py-8" data-testid="notif-empty">No notifications yet.</p>
          )}
          {items.map((n) => (
            <button
              key={n.notification_id}
              onClick={() => openNotif(n)}
              className={`w-full text-left px-4 py-3 border-b border-sage/10 hover:bg-sage-light/60 transition-colors ${!n.read ? "bg-terracotta-light/40" : ""}`}
              data-testid={`notif-item-${n.notification_id}`}
            >
              <div className="flex items-start gap-2">
                {!n.read && <span className="w-2 h-2 rounded-full bg-terracotta mt-1.5 shrink-0" />}
                <div className={n.read ? "pl-4" : ""}>
                  <p className="text-sm font-bold text-forest">{n.title}</p>
                  <p className="text-xs text-forest/60 mt-0.5">{n.body}</p>
                  <p className="text-[10px] text-forest/40 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function RoleStatusBadge({ role }) {
  const live = role.role === "parent" || role.vendor_status === "approved";
  return (
    <span
      className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${live ? "bg-sage-light text-sage-dark" : "bg-amber-100 text-amber-800"}`}
      data-testid={`role-status-badge-${role.role}`}
    >
      {live ? "Live" : "Pending"}
    </span>
  );
}

function RoleSwitcher({ variant = "desktop" }) {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Real role from the DB (not the impersonation shadow). An admin that is
  // currently "viewing as" a non-admin role still exposes their real admin
  // identity so we can show them the admin-view-as switcher.
  const realRole = user?.admin_role || user?.role;
  const isAdmin = realRole === "admin";

  useEffect(() => {
    if (!user || isAdmin) return;
    api.get("/auth/my-roles")
      .then((r) => setRoles(r.data.roles || []))
      .catch(() => setRoles([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  if (!user) return null;

  // Admin: expose the view-as options (Parent / Vendor / Event Creator /
  // Marketplace) inside the standard switcher so admins can hop between
  // perspectives without a dedicated section on the Admin dashboard.
  const adminViewAsRoles = [
    { role: "admin", label: "Admin" },
    { role: "parent", label: "Parent" },
    { role: "vendor", label: "Vendor" },
    { role: "event_creator", label: "Event Creator" },
    { role: "marketplace_user", label: "Marketplace Seller" },
  ];

  // Non-admin users only see the switcher if they have more than one profile.
  if (!isAdmin && roles.length < 2) return null;

  const currentRole = user.role;
  const currentLabel = isAdmin
    ? (adminViewAsRoles.find((r) => r.role === currentRole)?.label || "Admin")
    : (roles.find((r) => r.is_current)?.label
       || (user.role || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()));

  const pick = async (role) => {
    if (busy || role === currentRole) { setOpen(false); return; }
    setBusy(true);
    try {
      if (isAdmin) {
        await api.post("/auth/admin-view-as", { role });
        const fresh = await refresh();
        setOpen(false);
        toast.success(role === "admin" ? "Back in admin view" : `Now viewing Compass Kids as ${role.replace(/_/g, " ")}`);
        if (fresh) navigate(dashboardFor(fresh));
      } else {
        await api.post("/auth/switch-role", { role });
        const fresh = await refresh();
        setOpen(false);
        toast.success(`Switched to ${role.replace(/_/g, " ")} profile`);
        if (fresh) navigate(dashboardFor(fresh));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not switch role");
    } finally {
      setBusy(false);
    }
  };

  const items = isAdmin
    ? adminViewAsRoles.map((r) => ({ ...r, is_current: r.role === currentRole }))
    : roles;

  const badge = (r) => (r.bundle_member && !isAdmin ? <RoleStatusBadge role={r} /> : null);

  if (variant === "mobile") {
    return (
      <div className="border border-sage/20 rounded-2xl p-2 bg-white/70" data-testid="role-switcher-mobile">
        <p className="text-[10px] font-bold uppercase tracking-wide text-forest/50 px-2 pb-1">
          {isAdmin ? "View Compass Kids as" : "Switch profile"}
        </p>
        <div className="flex flex-col">
          {items.map((r) => (
            <button
              key={r.role}
              onClick={() => pick(r.role)}
              disabled={busy}
              className={`flex items-center justify-between text-left px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${r.is_current ? "bg-sage-light text-sage-dark" : "text-forest/80 hover:bg-softstone/70"}`}
              data-testid={`role-switcher-mobile-${r.role}`}
            >
              <span className="flex items-center gap-2">{r.label}{badge(r)}</span>
              {r.is_current && <Check className="w-4 h-4 text-sage-dark" />}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="rounded-full border-sage/40 text-forest hover:bg-sage-light hover:text-forest"
          data-testid="role-switcher-btn"
        >
          <Repeat className="w-4 h-4 mr-2" /> {currentLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 rounded-2xl bg-cream border-sage/20 p-2 shadow-lg" data-testid="role-switcher-panel">
        <p className="text-[10px] font-bold uppercase tracking-wide text-forest/50 px-3 py-1">
          {isAdmin ? "View Compass Kids as" : "Your profiles"}
        </p>
        <div className="flex flex-col">
          {items.map((r) => (
            <button
              key={r.role}
              onClick={() => pick(r.role)}
              disabled={busy}
              className={`flex items-center justify-between px-3 py-2 rounded-xl text-sm font-semibold text-left transition-colors ${r.is_current ? "bg-sage-light text-sage-dark" : "text-forest/80 hover:bg-softstone/70"}`}
              data-testid={`role-switcher-option-${r.role}`}
            >
              <span className="flex items-center gap-2">{r.label}{badge(r)}</span>
              {r.is_current && <Check className="w-4 h-4 text-sage-dark" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Navbar() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [returning, setReturning] = useState(false);

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  const returnToAdmin = async () => {
    setReturning(true);
    try {
      await api.post("/auth/admin-view-as", { role: "admin" });
      const fresh = await refresh();
      toast.success("Back in admin view");
      navigate(fresh ? dashboardFor(fresh) : "/admin");
    } catch {
      toast.error("Could not return to admin view");
    } finally {
      setReturning(false);
    }
  };

  const roleLinks = linksFor(user);

  const linkCls = (to) => {
    const [toPath, toSearch = ""] = to.split("?");
    const toParams = new URLSearchParams(toSearch);
    const curParams = new URLSearchParams(location.search);
    const pathMatches = location.pathname === toPath || location.pathname.startsWith(toPath + "/");
    // Every query param on the target link must match the current URL for this
    // link to be considered active (e.g. /resources?tab=directory only lights up
    // when ?tab=directory is actually present).
    const paramsMatch = Array.from(toParams.entries()).every(([k, v]) => curParams.get(k) === v);
    // If a sibling link in the nav shares the same pathname but adds a
    // distinguishing query param (e.g. Marketplace adds ?tab=directory on top
    // of /resources), a plain link to the same pathname must NOT light up when
    // that distinguishing param is present on the current URL — otherwise both
    // the plain link and the distinguished sibling would appear active at once.
    const hasActiveSibling = roleLinks.some((l) => {
      if (l.to === to) return false;
      const [sPath, sSearch = ""] = l.to.split("?");
      if (sPath !== toPath || !sSearch) return false;
      const sParams = new URLSearchParams(sSearch);
      return Array.from(sParams.entries()).every(([k, v]) => curParams.get(k) === v);
    });
    const active = pathMatches && paramsMatch && !hasActiveSibling;
    return `text-sm font-semibold transition-colors hover:text-terracotta-dark ${
      active ? "text-terracotta-dark" : "text-forest/80"
    }`;
  };

  // Admin impersonation ribbon — pinned above every variant of the navbar so
  // the admin can always get back to their own view, even on pages whose
  // Navbar branch (setup, subscribe lockdown) hides the normal controls.
  const impersonationRibbon = (user && user.admin_impersonating) ? (
    <div
      className="bg-terracotta text-white"
      data-testid="admin-impersonation-ribbon"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex flex-wrap items-center gap-3 text-xs sm:text-sm">
        <Repeat className="w-4 h-4 shrink-0" />
        <span className="font-bold">
          Admin — viewing as {(user.role || "").replace(/_/g, " ")}
        </span>
        <span className="hidden sm:inline text-white/80">
          Everything on this page shows what a {(user.role || "").replace(/_/g, " ")} would see.
        </span>
        <Button
          size="sm"
          variant="outline"
          className="ml-auto rounded-full border-white/40 bg-white/10 hover:bg-white/20 text-white text-xs h-7"
          onClick={returnToAdmin}
          disabled={returning}
          data-testid="admin-impersonation-return-btn"
        >
          {returning ? "Switching..." : "Return to admin"}
        </Button>
      </div>
    </div>
  ) : null;

  // Profile-setup mode: strip the navbar down to just the Compass Kids logo + name.
  // No menu items, no dashboard button, no profile, no sign-out, no notifications.
  if (isSetupStep(user, location.pathname)) {
    return (
      <>
        {impersonationRibbon}
        <header className="sticky top-0 z-50 bg-cream/95 backdrop-blur border-b border-sage/20" data-testid="navbar-setup">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center">
            <Link to="/" className="flex items-center gap-2 select-none" data-testid="nav-logo">
              <img src="/logo.png" alt="Compass Kids logo" className="w-10 h-10 rounded-full object-cover border border-terracotta/30" data-testid="nav-logo-img" />
              <span className="font-heading font-black text-xl text-forest">Compass Kids</span>
            </Link>
          </div>
        </header>
      </>
    );
  }

  // Subscribe + checkout lockdown (parent, vendor, eventcreator, bundle):
  // while an authenticated non-admin user hasn't completed payment, the top
  // bar on /subscribe or /checkout?plan=parent|vendor|eventcreator|bundle
  // shows ONLY the logo + a Sign out button. Role links, Dashboard, Profile,
  // notifications, and the mobile hamburger stay hidden until
  // subscription_status === "active".
  const checkoutPlan =
    location.pathname === "/checkout"
      ? new URLSearchParams(location.search).get("plan")
      : null;
  const isLockedCheckout =
    checkoutPlan === "parent" ||
    checkoutPlan === "vendor" ||
    checkoutPlan === "eventcreator" ||
    checkoutPlan === "bundle" ||
    checkoutPlan === "marketplace";
  if (
    (location.pathname === "/subscribe" || isLockedCheckout) &&
    user &&
    user.role !== "admin" &&
    user.subscription_status !== "active"
  ) {
    return (
      <>
        {impersonationRibbon}
        <header className="sticky top-0 z-50 bg-cream/95 backdrop-blur border-b border-sage/20" data-testid="navbar-subscribe">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 select-none" data-testid="nav-logo">
              <img src="/logo.png" alt="Compass Kids logo" className="w-10 h-10 rounded-full object-cover border border-terracotta/30" data-testid="nav-logo-img" />
              <span className="font-heading font-black text-xl text-forest">Compass Kids</span>
            </Link>
            <Button
              variant="ghost"
              className="rounded-full text-forest/70 hover:text-terracotta-dark"
              onClick={handleLogout}
              data-testid="nav-subscribe-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </header>
      </>
    );
  }

  // Guest lockdown (every route): until the user signs in, the top bar shows
  // ONLY the logo + Sign in + Sign Up buttons. All other nav items — role
  // links, Dashboard, Profile, Sign out, notifications bell, mobile hamburger
  // — are hidden until authenticated. Once a user signs in the full navbar
  // below renders normally.
  if (!user) {
    return (
      <header className="sticky top-0 z-50 bg-cream/95 backdrop-blur border-b border-sage/20" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 select-none" data-testid="nav-logo">
            <img src="/logo.png" alt="Compass Kids logo" className="w-10 h-10 rounded-full object-cover border border-terracotta/30" data-testid="nav-logo-img" />
            <span className="font-heading font-black text-xl text-forest">Compass Kids</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3" data-testid="nav-home-auth-ctas">
            <Button variant="ghost" className="rounded-full text-forest hover:text-terracotta-dark" onClick={() => navigate("/login")} data-testid="nav-login-btn">
              Sign in
            </Button>
            <Button className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white shadow-md hover:-translate-y-0.5 transition-transform" onClick={() => navigate("/register")} data-testid="nav-join-btn">
              Sign Up
            </Button>
          </div>
        </div>
      </header>
    );
  }

  return (
    <>
      {impersonationRibbon}
      <header className="sticky top-0 z-50 bg-cream/95 backdrop-blur border-b border-sage/20" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 select-none" data-testid="nav-logo">
          <img src="/logo.png" alt="Compass Kids logo" className="w-10 h-10 rounded-full object-cover border border-terracotta/30" data-testid="nav-logo-img" />
          <span className="font-heading font-black text-xl text-forest">Compass Kids</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8" data-testid={`nav-links-${user?.role || "guest"}`}>
          {roleLinks.map((l) => (
            <Link key={l.to} to={l.to} className={linkCls(l.to)} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              {user.role !== "admin" &&
                user.subscription_status !== "active" &&
                !user.bundle_member &&
                user.partner_role !== "secondary" &&
                !(["vendor", "event_creator", "marketplace_user"].includes(user.role) && user.vendor_status === "approved") && (
                <Button size="sm" className="rounded-full bg-amber-400 hover:bg-amber-500 text-forest font-bold" onClick={() => navigate("/subscribe")} data-testid="nav-subscribe-btn">
                  Subscribe
                </Button>
              )}
              <RoleSwitcher variant="desktop" />
              {user.bundle_member && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-terracotta-light text-terracotta-dark border border-terracotta/30 px-2.5 py-1 text-[11px] font-black uppercase tracking-wide shadow-sm"
                  data-testid="bundle-member-pill"
                  title="All four roles are unlocked for this account"
                >
                  <Sparkles className="w-3 h-3" /> Bundle member
                </span>
              )}
              <Button
                variant="outline"
                className="rounded-full border-sage/40 text-forest hover:bg-sage-light hover:text-forest"
                onClick={() => navigate(dashboardFor(user))}
                data-testid="nav-dashboard-btn"
              >
                <LayoutDashboard className="w-4 h-4 mr-2" />
                {user.role === "vendor" ? "Vendor Portal" : user.role === "event_creator" ? "Event Portal" : user.role === "marketplace_user" ? "Marketplace Portal" : user.role === "admin" ? "Admin" : "Dashboard"}
              </Button>
              <Button
                variant="ghost"
                className="rounded-full text-forest/70 hover:text-terracotta-dark"
                onClick={() => navigate("/profile")}
                data-testid="nav-profile-btn"
              >
                <UserCircle className="w-4 h-4 mr-2" /> Profile
              </Button>
              <Button
                variant="ghost"
                className="rounded-full text-forest/70 hover:text-terracotta-dark"
                onClick={handleLogout}
                data-testid="nav-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" className="rounded-full text-forest hover:text-terracotta-dark" onClick={() => navigate("/login")} data-testid="nav-login-btn">
                Sign in
              </Button>
              <Button className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white shadow-md hover:-translate-y-0.5 transition-transform" onClick={() => navigate("/register")} data-testid="nav-join-btn">
                Sign Up
              </Button>
            </>
          )}
        </div>

        <div className="flex items-center gap-1">
          <NotificationsBell />
          <button className="md:hidden p-2 text-forest" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Toggle menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-sage/20 bg-cream px-4 py-4 space-y-3" data-testid="nav-mobile-menu">
          {roleLinks.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block text-sm font-semibold text-forest/80 py-1" data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {l.label}
            </Link>
          ))}
          <div className="pt-2 border-t border-sage/20 flex flex-col gap-2">
            {user ? (
              <>
                <RoleSwitcher variant="mobile" />
                {user.bundle_member && (
                  <span
                    className="inline-flex items-center justify-center gap-1 rounded-full bg-terracotta-light text-terracotta-dark border border-terracotta/30 px-3 py-1.5 text-[11px] font-black uppercase tracking-wide shadow-sm"
                    data-testid="bundle-member-pill-mobile"
                  >
                    <Sparkles className="w-3 h-3" /> Bundle member
                  </span>
                )}
                <Button className="rounded-full bg-sage hover:bg-sage-dark text-white" onClick={() => { setOpen(false); navigate(dashboardFor(user)); }} data-testid="nav-mobile-dashboard-btn">
                  My {user.role === "vendor" ? "Vendor Portal" : user.role === "event_creator" ? "Event Portal" : user.role === "marketplace_user" ? "Marketplace Portal" : user.role === "admin" ? "Admin" : "Dashboard"}
                </Button>
                <Button variant="outline" className="rounded-full" onClick={() => { setOpen(false); navigate("/profile"); }} data-testid="nav-mobile-profile-btn">My profile</Button>
                <Button variant="outline" className="rounded-full" onClick={handleLogout} data-testid="nav-mobile-logout-btn">Sign out</Button>
              </>
            ) : (
              <>
                <Button variant="outline" className="rounded-full" onClick={() => { setOpen(false); navigate("/login"); }} data-testid="nav-mobile-login-btn">Sign in</Button>
                <Button className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white" onClick={() => { setOpen(false); navigate("/register"); }} data-testid="nav-mobile-join-btn">Sign Up</Button>
              </>
            )}
          </div>
        </div>
      )}
      </header>
    </>
  );
}
